<template>
  <div class="d-flex flex-column row-gap-4 h-100">
    <label for="description" class="text--bold text-text-secondary">{{ $t(`${TRANSLATION_KEY_PREFIX}.descriptionFieldLabel`) }}</label>
    <div class="position-relative d-flex h-100 description-wrapper">
      <VisualTextEditor
        v-model:value="descriptionValue"
        :class="{
          'is-editing': isDescriptionEditing,
          'is-read-only': isDescriptionFieldReadOnly,
        }"
        :clean-content-on-blur="false"
        :disabled="isDescriptionFieldDisabled"
        :error-messages="descriptionErrors"
        :max-length="DESCRIPTION_MAX_LENGTH"
        :placeholder="$t(`${TRANSLATION_KEY_PREFIX}.descriptionFieldPlaceholder`)"
        :read-only="isDescriptionFieldReadOnly"
        class="description-editor"
        counter="footer"
        data-locator="description-editor"
        keep-toolbar-on-blur
        @blur="updateDescriptionEditing"
        @focus="updateDescriptionEditing(true)"
      />
      <app-button v-if="showDescriptionButton"
        :disabled="!isDescriptionChanged || isDescriptionReachingMaxLength"
        :loading="isTaskLoading"
        class="position-absolute bottom-0 right-0 mb-3 mr-4"
        data-locator="update-description-button"
        size="small"
        type="outlined"
        variant="neutral"
        @click="updateTaskDescription"
      >
        {{ $t(`${TRANSLATION_KEY_PREFIX}.descriptionButtonLabel`) }}
      </app-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'

const DESCRIPTION_MAX_LENGTH = 3000
const TRANSLATION_KEY_PREFIX = 'project.checklist.panes.ChecklistPaneDetailsDescription'

export default {
  name: 'ChecklistPaneDetailsDescription',
  components: { VisualTextEditor },
  emits: ['data-unsaved', 'get-task-details', 'update-task','update:modelValue'],
  computed: {
    ...mapGetters('room', ['isCurrentUserPm']),
    ...mapState('checklist', ['checklistPane', 'editTaskPending', 'paneTaskDetailsPendingIds']),
    canUpdateDescription () {
      return this.isCurrentUserPm && this.currentTask
    },
    descriptionErrors () {
      const errors = []

      if (this.isDescriptionReachingMaxLength) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    descriptionLength () {
      return this.descriptionValue.length
    },
    isDescriptionChanged () {
      return this.descriptionValue !== this.details?.description
    },
    isDescriptionReachingMaxLength () {
      return this.descriptionLength === DESCRIPTION_MAX_LENGTH && this.isDescriptionEditing
    },
    isDescriptionFieldDisabled () {
      return !this.details
      || this.paneTaskDetailsPendingIds.includes(this.currentTask?.id)
    },
    isDescriptionFieldReadOnly () {
      return !this.isCurrentUserPm
    },
    isTaskLoading () {
      return this.editTaskPending === this.currentTask?.id
    },
    showDescriptionButton () {
      return this.canUpdateDescription && this.isDescriptionEditing
    },
  },
  props: {
    currentTask: {
      type: Object,
      default: null,
    },
    details: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    DESCRIPTION_MAX_LENGTH,
    TRANSLATION_KEY_PREFIX,
    descriptionValue: '',
    isDescriptionEditing: false,
  }),
  watch: {
    currentTask: {
      immediate: true,
      handler () {
        this.resetForm()
      },
    },
    details: {
      immediate: true,
      handler (val) {
        this.resetForm()

        if (val) {
          this.descriptionValue = val.description
        }
      },
    },
  },
  beforeUnmount() {
    this.resetForm()
  },
  methods: {
    resetForm() {
      this.descriptionValue = ''
      this.isDescriptionEditing = false
    },
    updateDescriptionEditing(isEditing) {
      if(this.isDescriptionFieldReadOnly) return

      this.isDescriptionEditing = this.isDescriptionChanged || isEditing
      this.$emit('data-unsaved', this.isDescriptionChanged ? this.checklistPane?.tab : null)
    },
    updateTaskDescription() {
      if(this.isDescriptionChanged) {
        this.$emit('update-task', { description : this.descriptionValue.trim() })
      }
    },
  },
};
</script>

<style scoped>
.description-wrapper {
  max-height: calc(100% - 48px);
}
.description-editor {
  outline: 1px solid rgb(var(--v-theme-border-secondary));
  border-radius: 12px;
  overflow: hidden;

  &.is-editing {
    outline: 2px solid rgb(var(--v-theme-info-darken-1));
  }

  &.is-read-only {
    outline: 1px solid rgb(var(--v-theme-grey-200));
  }
}
:deep(.editor__menubar) {
  border: none !important;
  padding: 4px 0;
}
:deep(.editor__content) {
  height: 100%;
}
:deep(.editor__content .ProseMirror) {
  border: none !important;
}
:deep(.description-editor.is-editing .editor__content) {
  padding-bottom: 48px;
}
:deep(.editor__footer) {
  flex-direction: column;
}
:deep(.text-counter) {
  bottom: 16px;
  left: 16px;
  position: absolute;

  .description-editor:not(.is-editing) & {
    opacity: 0;
  }
}
:deep(.tiptap){
  border: none;
  height: 100%;
  max-height: calc(100% - 36px);
}
</style>
